import type { Workspace } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import type { UseWorkspacesByOwnerReturn } from './types';

interface WorkspacesQueryLike {
  data?: { pages: unknown[] };
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetching: boolean;
}

export interface UseCombinedWorkspacesParams {
  defaultWorkspaceQuery?: Partial<WorkspacesQueryLike>;
  workspacesQuery: WorkspacesQueryLike;
  workspacesByOwner?: UseWorkspacesByOwnerReturn;
  searchText: string;
}

export interface UseCombinedWorkspacesReturn {
  allWorkspaces: Workspace[];
  isLoadingMore: boolean;
  onLoadMore?: () => void;
  isLoadingSearch: boolean;
}

export const useCombinedWorkspaces = (params: UseCombinedWorkspacesParams): UseCombinedWorkspacesReturn => {
  const { defaultWorkspaceQuery, workspacesQuery, workspacesByOwner, searchText } = params;

  // Combine default workspace with other workspaces
  const defaultWorkspace = defaultWorkspaceQuery?.data?.pages[0] as Workspace | undefined;
  const otherWorkspaces = (
    workspacesByOwner ? workspacesByOwner.workspacesByOwner : workspacesQuery.data?.pages || []
  ) as Workspace[];

  // Filter out default workspace from other workspaces to avoid duplication
  const filteredOtherWorkspaces = defaultWorkspace
    ? otherWorkspaces.filter((workspace) => workspace.name !== defaultWorkspace.name)
    : otherWorkspaces;

  // Combine workspaces with default first
  const combinedWorkspaces = defaultWorkspace
    ? [defaultWorkspace, ...filteredOtherWorkspaces]
    : filteredOtherWorkspaces;

  return workspacesByOwner
    ? {
        allWorkspaces: combinedWorkspaces,
        isLoadingMore: workspacesByOwner.isLoadingMore || false,
        onLoadMore: undefined,
        isLoadingSearch: false,
      }
    : {
        allWorkspaces: combinedWorkspaces,
        isLoadingMore: workspacesQuery.isFetchingNextPage,
        onLoadMore: workspacesQuery.hasNextPage ? workspacesQuery.fetchNextPage : undefined,
        isLoadingSearch: Boolean(searchText && workspacesQuery.isFetching),
      };
};
